
import Vue, { PropType } from 'vue';

import { CRS, LatLng, latLng, BoundsLiteral, MapOptions, LatLngTuple, LatLngBounds } from 'leaflet';
import { LImageOverlay, LMap } from 'vue2-leaflet';

import FileUpload from '@/components/configuration/FileUpload.vue';
import DeviceMarker from '@/components/common/DeviceMarker.vue';
import Modal from '@/components/modal/Modal.vue';
import { FloorEditData, Device } from '@/interfaces/locations';
import { DeviceMarkerMap } from '../configuration/DeviceMap.vue';

interface LeafletInfo {
	mapComponent: LMap | null;
	mapFile: File | string;
	center: LatLng;
	defaultBounds: BoundsLiteral;
	bounds: BoundsLiteral;
	crs: CRS;
	zoom: number;
	options: MapOptions;
}

export default Vue.extend({
	components: {
		Modal,
		FileUpload,
		DeviceMarker,
	},

	data(): {
		leafletInfo: LeafletInfo;
		errorMessage: string;
	} {
		return {
			leafletInfo: {
				mapComponent: null,
				mapFile: '',
				center: latLng(100, -100),
				defaultBounds: [
					[100, -100],
					[-100, 100],
				],
				bounds: [
					[100, -100],
					[-100, 100],
				],
				crs: CRS.Simple,
				zoom: 2,
				options: {
					attributionControl: false,
					zoomControl: false,
					dragging: false,
					keyboard: false,
					scrollWheelZoom: false,
					doubleClickZoom: false,
					boxZoom: false,
				},
			},
			errorMessage: '',
		};
	},

	props: {
		deviceEditData: { type: Object as PropType<FloorEditData> },
		devices: { type: Array as PropType<Device[]>, default: (): Device[] => [] },
	},

	computed: {
		markers(): DeviceMarkerMap[] {
			return this.devices.map((l) => {
				return {
					id: l.id,
					position: { lat: l.local_latitude, lng: l.local_longitude },
					name: l.name,
					active: false,
					draggable: false,
					visible: true,
					isLuminaire: false,
					isMovementSensor: false,
					isLightLevelSensor: false,
					isGateway: false,
					isOtherSensor: false,
				};
			});
		},
	},

	methods: {
		show(): void {
			this.$bvModal.show('modal-map');
		},

		hide(): void {
			this.$bvModal.hide('modal-map');
		},

		resetMapModal(): void {
			// Used for resetting modal data. Typescript doesn't like this function for some reason.
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			Object.assign(this.$data, (this.$options.data as any).call(this));
		},

		confirmMap(): void {
			if (!this.leafletInfo.mapFile) {
				this.errorStatus(this.$t('error_file_upload_missing_file').toString());
				return;
			}

			if (this.errorMessage) {
				this.errorStatus('');
			}

			this.$emit('confirmMap', {
				map: this.leafletInfo.mapFile,
				bounds: this.leafletInfo.bounds,
				id: this.deviceEditData.parent.id,
			});
		},

		mapReady(): void {
			this.leafletInfo.mapComponent = this.$refs.map as LMap;
			if (this.deviceEditData.parent.image) {
				this.uploadMap(this.deviceEditData.parent.image, this.deviceEditData.parent.image_bounds);
			}
			this.leafletInfo.mapComponent!.mapObject.setView(
				this.leafletInfo.center,
				this.leafletInfo.zoom
			);
		},

		errorStatus(errorMessage: string): void {
			this.errorMessage = errorMessage;
		},

		async getScaledBounds(file: string, defaultBounds: BoundsLiteral): Promise<BoundsLiteral> {
			const bounds: BoundsLiteral = [[...defaultBounds[0]], [defaultBounds[1][0], 0]];
			const img = new Image();
			img.src = file;
			await new Promise((resolve) => setTimeout(resolve, 10));

			const originalHeight = img.naturalHeight;
			const originalWidth = img.naturalWidth;

			const scalingFactor = Math.abs(defaultBounds[0][0] - defaultBounds[1][0]) / originalHeight;

			const xSize = originalWidth * scalingFactor;

			bounds[1][1] = defaultBounds[0][1] + xSize;

			return bounds;
		},

		async uploadMap(file: string, bounds?: BoundsLiteral): Promise<void> {
			this.errorStatus('');
			this.leafletInfo.mapFile = file;

			if (bounds) {
				this.leafletInfo.bounds = bounds;
			} else {
				this.leafletInfo.bounds = await this.getScaledBounds(file, this.leafletInfo.defaultBounds);
			}
		},

		onFloorMove(direction: LatLngTuple): void {
			this.setFloorBounds(this.translate(direction));
		},

		onFloorZoom(zoomLevel: number): void {
			this.setFloorBounds(this.translateZoom(zoomLevel));
		},

		translate(direction: LatLngTuple): LatLngBounds {
			this.leafletInfo.bounds[0][0] += direction[0];
			this.leafletInfo.bounds[0][1] += direction[1];
			this.leafletInfo.bounds[1][0] += direction[0];
			this.leafletInfo.bounds[1][1] += direction[1];

			return new LatLngBounds(this.leafletInfo.bounds);
		},

		translateZoom(zoomLevel: number): LatLngBounds {
			const bounds = this.leafletInfo.bounds;
			const height = Math.abs(bounds[0][0] - bounds[1][0]);
			const width = Math.abs(bounds[0][1] - bounds[1][1]);
			const aspectRatio = width / height;

			this.leafletInfo.bounds[0][0] += zoomLevel / aspectRatio;
			this.leafletInfo.bounds[0][1] -= zoomLevel;
			this.leafletInfo.bounds[1][0] -= zoomLevel / aspectRatio;
			this.leafletInfo.bounds[1][1] += zoomLevel;

			return new LatLngBounds(this.leafletInfo.bounds);
		},

		setFloorBounds(bounds: LatLngBounds): void {
			// This functions expects Bounds, but in reality this does not work
			(this.$refs['leaflet-image-overlay'] as LImageOverlay).setBounds(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				bounds as any
			);
		},
	},
});
